import './SvgPdsMarkerIcon.scss'

import useInvokeAsync from 'components/App/hooks/useInvokeAsync'
import useTheme, { Theme } from 'components/App/hooks/useTheme'
import { getPoiMarkerId, MarkerState, MarkerType, Theme as PoiMarkerTheme } from 'lib/cartography'
import React, { useCallback } from 'react'

import SvgMarkerIcon from '../SvgMarkerIcon'

interface SvgPdsMarkerIconProps {
  className?: string
  style?: React.CSSProperties
  width?: string
  height?: string
  categoryId?: string
  type?: MarkerType
  isHovered?: boolean
  isSelected?: boolean
  innerColor: string
  isStared?: boolean
  onHover?: (hoverStatus: boolean) => void
  onSelect?: (hoverStatus: boolean) => void
}

export default function SvgPdsMarkerIcon({
  className,
  categoryId,
  type = MarkerType.poi,
  isHovered,
  isSelected = true,
  isStared = false,
}: SvgPdsMarkerIconProps): JSX.Element | null {
  const [theme, _] = useTheme()
  const markerTheme = toPoiMarkerTheme(theme)
  const { result } = useInvokeAsync(
    useCallback(async () => {
      const iconState =
        isHovered !== undefined && isHovered
          ? MarkerState.hoverSpotlight
          : isSelected !== undefined && isSelected
          ? MarkerState.selectedSpotlight
          : MarkerState.defaultSpotlight
      const result = await getPoiMarkerId(categoryId, type, markerTheme, iconState)
      return result
    }, [categoryId, isHovered, isSelected, markerTheme, type])
  )
  const spriteId = result as string | undefined
  const [svgWidth, svgHeight] = getMarkerSize(spriteId, isSelected, isStared)
  const markerStyle = getMarkerStyle(Number(svgWidth), Number(svgHeight))

  return (
    <SvgMarkerIcon
      className={className}
      style={markerStyle}
      isStared={isStared}
      height={svgHeight}
      width={svgWidth}
    >
      {spriteId !== undefined && <use href={`#${spriteId}`} />}
    </SvgMarkerIcon>
  )
}

function getMarkerSize(
  svgId?: string,
  hasSelectedState?: boolean,
  hasStarState?: boolean
): [string, string] {
  const svgSize =
    svgId !== undefined
      ? document
          .getElementById(svgId)
          ?.getAttribute('viewBox')
          ?.split(' ')
          .filter((r) => Number(r))
      : undefined
  const finalHeight = svgSize !== undefined ? svgSize[1] : String(32)
  const finalWidth =
    svgSize !== undefined
      ? hasStarState !== undefined && hasStarState
        ? hasSelectedState !== undefined && hasSelectedState
          ? Number(svgSize[0]) + 4
          : Number(svgSize[0])
        : Number(svgSize[0])
      : 42

  return [String(finalWidth), finalHeight]
}

function getMarkerStyle(width: number, height: number): React.CSSProperties {
  return {
    left: `-${width / 2}px`,
    top: `-${height}px`,
  }
}

function toPoiMarkerTheme(theme: Theme): PoiMarkerTheme {
  switch (theme) {
    case Theme.dark:
      return PoiMarkerTheme.dark
    case Theme.light:
      return PoiMarkerTheme.light
    case Theme.hybrid:
      return PoiMarkerTheme.hybrid
  }
}
